import { useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, Trash2, X, ZoomIn, ImagePlus, ClipboardList } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  appointmentId: number;
  salonId: number;
  canEdit?: boolean;
}

type PhotoType = "before" | "after" | "anamnese";

export function AppointmentPhotoGallery({ appointmentId, salonId, canEdit = true }: Props) {
  const utils = trpc.useUtils();
  const { data: photos = [], isLoading } = trpc.appointmentPhotos.list.useQuery({ appointmentId });
  const uploadMutation = trpc.appointmentPhotos.upload.useMutation({
    onSuccess: () => {
      utils.appointmentPhotos.list.invalidate({ appointmentId });
      toast.success("Foto adicionada com sucesso!");
    },
    onError: (e) => toast.error("Erro ao enviar foto: " + e.message),
  });
  const deleteMutation = trpc.appointmentPhotos.delete.useMutation({
    onSuccess: () => {
      utils.appointmentPhotos.list.invalidate({ appointmentId });
      toast.success("Foto removida.");
    },
    onError: (e) => toast.error("Erro ao remover foto: " + e.message),
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingType, setPendingType] = useState<PhotoType>("before");
  const [lightbox, setLightbox] = useState<string | null>(null);

  const beforePhotos = photos.filter((p) => p.type === "before");
  const afterPhotos = photos.filter((p) => p.type === "after");
  const anamnesePhotos = photos.filter((p) => p.type === "anamnese");

  function handleAddPhoto(type: PhotoType) {
    setPendingType(type);
    fileInputRef.current?.click();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Validar tamanho (máx 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Imagem muito grande. Máximo 10MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      uploadMutation.mutate({
        appointmentId,
        salonId,
        type: pendingType,
        dataUrl,
        fileName: file.name,
      });
    };
    reader.readAsDataURL(file);
    // Limpar input para permitir re-upload do mesmo arquivo
    e.target.value = "";
  }

  function handleDelete(photoId: number) {
    if (!confirm("Remover esta foto?")) return;
    deleteMutation.mutate({ photoId });
  }

  return (
    <div className="space-y-4">
      {/* Input de arquivo oculto — abre câmera no mobile */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Seção Ficha de Anamnese */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide flex items-center gap-1">
            <ClipboardList className="w-3.5 h-3.5" /> Ficha de Anamnese
          </span>
          {canEdit && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs gap-1 border-purple-300 text-purple-700 hover:bg-purple-50"
              onClick={() => handleAddPhoto("anamnese")}
              disabled={uploadMutation.isPending}
            >
              <Camera className="w-3 h-3" />
              Adicionar
            </Button>
          )}
        </div>
        <PhotoGrid
          photos={anamnesePhotos}
          onDelete={canEdit ? handleDelete : undefined}
          onZoom={setLightbox}
          isDeleting={deleteMutation.isPending}
        />
        {anamnesePhotos.length === 0 && (
          <EmptySlot
            label="Nenhuma ficha de anamnese"
            onClick={canEdit ? () => handleAddPhoto("anamnese") : undefined}
            accent="purple"
          />
        )}
      </div>

      {/* Divisor */}
      <div className="border-t border-border/50" />

      {/* Seção Antes */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Antes</span>
          {canEdit && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs gap-1"
              onClick={() => handleAddPhoto("before")}
              disabled={uploadMutation.isPending}
            >
              <Camera className="w-3 h-3" />
              Adicionar
            </Button>
          )}
        </div>
        <PhotoGrid
          photos={beforePhotos}
          onDelete={canEdit ? handleDelete : undefined}
          onZoom={setLightbox}
          isDeleting={deleteMutation.isPending}
        />
        {beforePhotos.length === 0 && (
          <EmptySlot label="Nenhuma foto de antes" onClick={canEdit ? () => handleAddPhoto("before") : undefined} />
        )}
      </div>

      {/* Seção Depois */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Depois</span>
          {canEdit && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs gap-1"
              onClick={() => handleAddPhoto("after")}
              disabled={uploadMutation.isPending}
            >
              <Camera className="w-3 h-3" />
              Adicionar
            </Button>
          )}
        </div>
        <PhotoGrid
          photos={afterPhotos}
          onDelete={canEdit ? handleDelete : undefined}
          onZoom={setLightbox}
          isDeleting={deleteMutation.isPending}
        />
        {afterPhotos.length === 0 && (
          <EmptySlot label="Nenhuma foto de depois" onClick={canEdit ? () => handleAddPhoto("after") : undefined} />
        )}
      </div>

      {/* Upload em andamento */}
      {uploadMutation.isPending && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground animate-pulse">
          <div className="w-3 h-3 rounded-full bg-amber-400 animate-bounce" />
          Enviando foto...
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2"
            onClick={() => setLightbox(null)}
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={lightbox}
            alt="Foto ampliada"
            className="max-w-full max-h-full rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

function PhotoGrid({
  photos,
  onDelete,
  onZoom,
  isDeleting,
}: {
  photos: { id: number; url: string }[];
  onDelete?: (id: number) => void;
  onZoom: (url: string) => void;
  isDeleting: boolean;
}) {
  if (photos.length === 0) return null;
  return (
    <div className="grid grid-cols-3 gap-2">
      {photos.map((photo) => (
        <div key={photo.id} className="relative group aspect-square rounded-lg overflow-hidden border border-border bg-muted">
          <img
            src={photo.url}
            alt="Foto do procedimento"
            className="w-full h-full object-cover cursor-pointer"
            onClick={() => onZoom(photo.url)}
          />
          {/* Overlay de ações */}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <button
              className="bg-white/90 rounded-full p-1.5 text-gray-800 hover:bg-white"
              onClick={() => onZoom(photo.url)}
            >
              <ZoomIn className="w-3.5 h-3.5" />
            </button>
            {onDelete && (
              <button
                className="bg-red-500/90 rounded-full p-1.5 text-white hover:bg-red-600"
                onClick={() => onDelete(photo.id)}
                disabled={isDeleting}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptySlot({
  label,
  onClick,
  accent,
}: {
  label: string;
  onClick?: () => void;
  accent?: "purple";
}) {
  return (
    <button
      className={cn(
        "w-full border-2 border-dashed rounded-lg py-4 flex flex-col items-center gap-1 text-xs",
        accent === "purple"
          ? "border-purple-200 text-purple-400 hover:border-purple-400 hover:text-purple-600"
          : "border-border text-muted-foreground hover:border-amber-400 hover:text-amber-600",
        onClick ? "cursor-pointer transition-colors" : "cursor-default"
      )}
      onClick={onClick}
      disabled={!onClick}
    >
      <ImagePlus className="w-5 h-5" />
      {label}
    </button>
  );
}
